import type { ModalQueueGeneratedOpener } from '@carvana/merch-react-hooks';
import { useDebouncedCallback } from '@carvana/merch-react-hooks';
import { useCallback } from 'react';
import { useIsNative } from '~/context/common/DeviceContext';
import { useSelectedTrade } from '~/context/common/TradeContext';
import { useIsLoggedIn } from '~/context/common/UserContext';
import useTrackAppraisalCtaRendered from './useTrackAppraisalCtaRendered';

type OwnProps = {
  isEligible: boolean;
  setAppraisalEditAcquisitionName: (
    acquisitionName: string | undefined
  ) => void;
  setAppraisalEmbeddedModalOpen: ModalQueueGeneratedOpener;
  setAppraisalRefreshModalOpen: ModalQueueGeneratedOpener;
};

export default function useRefreshCtaClick({
  isEligible,
  setAppraisalEditAcquisitionName,
  setAppraisalEmbeddedModalOpen,
  setAppraisalRefreshModalOpen
}: OwnProps): React.MouseEventHandler<HTMLElement> {
  const isLoggedIn = useIsLoggedIn();
  const isNative = useIsNative();
  const selectedTrade = useSelectedTrade();
  const trackAppraisalCtaRendered = useTrackAppraisalCtaRendered();

  const editAcquisitionName = isEligible ? selectedTrade?.id : undefined;
  const canOpenRefreshModal = isLoggedIn && !isNative;

  const openModal = useCallback<React.MouseEventHandler<HTMLElement>>(() => {
    trackAppraisalCtaRendered();

    if (editAcquisitionName) {
      setAppraisalEditAcquisitionName(editAcquisitionName);
      setAppraisalEmbeddedModalOpen(true);

      return;
    }

    if (canOpenRefreshModal) setAppraisalRefreshModalOpen(true);
  }, [
    canOpenRefreshModal,
    editAcquisitionName,
    setAppraisalEditAcquisitionName,
    setAppraisalEmbeddedModalOpen,
    setAppraisalRefreshModalOpen,
    trackAppraisalCtaRendered
  ]);

  return useDebouncedCallback(openModal, [openModal], 500, {
    leading: true,
    onEveryCall: ev => {
      if (editAcquisitionName || canOpenRefreshModal) ev.preventDefault();
    },
    trailing: false
  });
}
